import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { Avatar, Skeleton, Tooltip, cn } from '@billda/design-system';

export interface UserAvatarProps {
  /**
   * Pre-computed initials to display
   * Takes priority over firstName/lastName and name
   */
  initials?: string;

  /**
   * First name - used with lastName to generate initials
   */
  firstName?: string;

  /**
   * Last name - used with firstName to generate initials
   */
  lastName?: string;

  /**
   * Full name - used to generate initials by taking first letter of each word
   */
  name?: string;

  /**
   * Visual variant of the avatar
   * - 'current': Primary background with contrast text (active user)
   * - 'inactive': Grey background with white text (inactive user)
   * - 'outlined': Default surface background with primary text (default, most common)
   */
  variant?: 'current' | 'inactive' | 'outlined';

  /**
   * Avatar size
   * - number: Custom pixel size
   * - 'small': 32px (compact UI)
   * - 'medium': 48px (default, standard size)
   * - 'large': 80px (profile headers)
   */
  size?: number | 'small' | 'medium' | 'large';

  /**
   * Border configuration
   * - true: Uses default '2px solid divider' colour
   * - 'onInverse': Muted border for inverse headers
   * - false/undefined: No border
   */
  border?: boolean | 'onInverse';

  /**
   * Click handler - when provided, wraps avatar in a button
   */
  onClick?: () => void;

  /**
   * Tooltip text - only shown when onClick is provided
   */
  tooltip?: string;

  /**
   * Size of the button wrapper (if different from avatar size)
   * Only applicable when onClick is provided
   */
  buttonSize?: number;

  /**
   * Shows loading skeleton instead of avatar
   */
  loading?: boolean;

  /**
   * Icon to show when no initials are available
   */
  fallbackIcon?: ReactNode;

  /**
   * CSS position property
   */
  position?: 'static' | 'absolute';

  /**
   * Absolute positioning values (only used when position='absolute')
   */
  absolutePosition?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };

  /**
   * Test ID for testing
   */
  testId?: string;

  /** Optional initials font size override. */
  fontSize?: CSSProperties['fontSize'];
}

const sizeMap = {
  small: 32,
  medium: 48,
  large: 80,
};

const fontSizeMap = {
  small: '0.75rem',
  medium: '1rem',
  large: '2rem',
};

const variantClass = {
  current: 'bg-surface-primary text-on-primary',
  inactive: 'bg-content-disabled text-on-inverse',
  outlined: 'bg-surface-default text-content-accent',
} as const;

const px = (value: number | string | undefined) =>
  typeof value === 'number' ? `${value}px` : value;

/**
 * Atom: Unified User Avatar Component (DS fork of shared-ui UserAvatar)
 *
 * Identical public API to @billda/shared-ui's UserAvatar, rendered with
 * design-system Avatar/Tooltip/Skeleton primitives instead of MUI.
 * Runtime dimensions remain explicit styles; visual variants use DS tokens.
 */
export const UserAvatar = ({
  initials: providedInitials,
  firstName,
  lastName,
  name,
  variant = 'outlined',
  size = 'medium',
  border,
  onClick,
  tooltip,
  buttonSize,
  loading = false,
  fallbackIcon,
  position = 'static',
  absolutePosition,
  testId,
  fontSize: fontSizeOverride,
}: UserAvatarProps) => {
  // Compute initials with priority: providedInitials > firstName+lastName > name
  const computedInitials = useMemo(() => {
    if (providedInitials) {
      return providedInitials;
    }
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase();
    }
    return '';
  }, [providedInitials, firstName, lastName, name]);

  // Resolve size
  const avatarSize = typeof size === 'number' ? size : sizeMap[size];
  const fontSize =
    fontSizeOverride ??
    (typeof size === 'number' ? undefined : fontSizeMap[size]);

  // Resolve border
  const borderStyle =
    border === true
      ? '2px solid var(--color-border-default)'
      : border === 'onInverse'
        ? '2px solid var(--color-border-on-inverse-muted)'
        : undefined;

  const avatarStyle = {
    border: borderStyle,
    fontSize,
    fontWeight: variant === 'outlined' ? 700 : 600,
  } as CSSProperties;

  // Loading state
  if (loading) {
    return (
      <Skeleton
        variant="circular"
        width={avatarSize}
        height={avatarSize}
        data-testid={testId ? `${testId}-loading` : 'user-avatar-loading'}
      />
    );
  }

  // Avatar element
  const avatarElement = (
    <Avatar
      size={avatarSize}
      className={cn('type-label', variantClass[variant])}
      style={avatarStyle}
      data-testid={onClick ? undefined : testId || 'user-avatar'}
    >
      {computedInitials || fallbackIcon}
    </Avatar>
  );

  // If onClick provided, wrap in a button
  if (onClick) {
    const button = (
      <button
        type="button"
        onClick={onClick}
        aria-label={[computedInitials, tooltip || 'User profile']
          .filter(Boolean)
          .join(', ')}
        data-testid={testId || 'user-avatar'}
        className="flex cursor-pointer items-center justify-center rounded-full p-0 transition-transform duration-200 ease-out hover:scale-110"
        style={{
          width: buttonSize || avatarSize + 16,
          height: buttonSize || avatarSize + 16,
          position,
          ...(position === 'absolute'
            ? Object.fromEntries(
                Object.entries(absolutePosition ?? {}).map(([k, v]) => [
                  k,
                  px(v),
                ])
              )
            : {}),
        }}
      >
        {avatarElement}
      </button>
    );

    if (tooltip) {
      return (
        <Tooltip content={tooltip} side="right">
          {button}
        </Tooltip>
      );
    }

    return button;
  }

  // Non-interactive avatar with absolute positioning support
  if (position === 'absolute') {
    return (
      <div
        style={{
          position: 'absolute',
          ...Object.fromEntries(
            Object.entries(absolutePosition ?? {}).map(([k, v]) => [k, px(v)])
          ),
        }}
      >
        {avatarElement}
      </div>
    );
  }

  return avatarElement;
};
